import * as React from 'react'

import { useState, useEffect } from 'react'
import { Button, Descriptions, Form, Input, InputNumber, Modal } from 'antd'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { EditOutlinedHover } from '../styled'
import { Discount } from '../types'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useUpdateDiscount } from '../queries'

type Props = Discount

const modalProps = (
  intl: IntlShape,
  {
    name,
    rate,
    description,
  }: { name: string; rate: number; description: string }
) => ({
  title: intl.formatMessage({
    id: 'Discount_Modal_Edit_Confirm_Title',
  }),
  icon: <ExclamationCircleOutlined />,
  content: (
    <Descriptions
      title={intl.formatMessage({
        id: 'Discount_Modal_Edit_Confirm_Text',
      })}
      column={1}
    >
      <Descriptions.Item label={intl.formatMessage({ id: 'Discount_Name' })}>
        {name}
      </Descriptions.Item>
      <Descriptions.Item label={intl.formatMessage({ id: 'Discount_Rate' })}>
        {rate}%
      </Descriptions.Item>
      <Descriptions.Item
        label={intl.formatMessage({ id: 'Discount_Description' })}
      >
        {description}
      </Descriptions.Item>
    </Descriptions>
  ),
  okText: intl.formatMessage({ id: 'Yes' }),
  cancelText: intl.formatMessage({ id: 'No' }),
  width: 448,
})

const EditDiscount = ({ id, name, rate, description }: Props) => {
  const [form] = Form.useForm()
  const updateDiscount = useUpdateDiscount()
  const [isModalVisible, setIsModalVisible] = useState(false)

  useEffect(() => {
    form.resetFields()
  }, [isModalVisible])

  const intl = useIntl()

  return (
    <>
      <Button
        type="text"
        onClick={() => setIsModalVisible(true)}
        icon={<EditOutlinedHover />}
      />
      <Modal
        title={<FormattedMessage id="Discount_Modal_Edit_Title" />}
        cancelText={<FormattedMessage id="cancel" />}
        onCancel={() => setIsModalVisible(false)}
        cancelButtonProps={{ loading: updateDiscount.isLoading }}
        okText={<FormattedMessage id="Submit" />}
        onOk={form.submit}
        okButtonProps={{ loading: updateDiscount.isLoading }}
        width={580}
        visible={isModalVisible}
      >
        <Form<{ name: string; rate: number; description: string }>
          id="editDiscountForm"
          form={form}
          layout="vertical"
          initialValues={{
            name,
            rate: (100 * rate.numerator) / rate.denominator,
            description,
          }}
          onFinish={v => {
            Modal.confirm({
              ...modalProps(intl, v),
              onOk: () => {
                updateDiscount.mutate(
                  {
                    ...v,
                    id,
                    rate: {
                      numerator: v.rate,
                      denominator: 100,
                    },
                  },
                  { onSuccess: () => setIsModalVisible(false) }
                )
              },
            })
          }}
        >
          <Form.Item
            name="name"
            label={<FormattedMessage id="Discount_Name" />}
            rules={[{ required: true }]}
            preserve={false}
          >
            <Input
              placeholder={intl.formatMessage({
                id: 'Discount_Name_Placeholder',
              })}
            />
          </Form.Item>
          <Form.Item
            name="rate"
            label={<FormattedMessage id="Discount_Rate" />}
            rules={[{ required: true }]}
            preserve={false}
          >
            <InputNumber
              min={1 as number}
              max={100 as number}
              step={1}
              precision={2}
              formatter={value => `${value}%`}
              parser={value => parseFloat(value!.replace('%', ''))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label={<FormattedMessage id="Discount_Description" />}
            rules={[{ required: true }]}
            preserve={false}
          >
            <Input.TextArea
              placeholder={intl.formatMessage({
                id: 'Discount_Description_Placeholder',
              })}
              rows={2}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default EditDiscount
